import { Side } from '@server/data-feed/schemas/output.schema';
import { IChartApi, ISeriesApi, SeriesMarker, Time } from 'lightweight-charts';
import { MutableRefObject } from 'react';

type MarkerOptions = {
  color: string;
};

export const createArrowMarker = <
  T extends { time: Time; price?: number | null }
>(
  data: T,
  chartRef: React.MutableRefObject<IChartApi | null>,
  candlestickSeriesRef: MutableRefObject<ISeriesApi<'Candlestick'> | null>,
  { color }: MarkerOptions
): HTMLDivElement | undefined => {
  const timeCoordinate = chartRef.current
    ?.timeScale()
    .timeToCoordinate(data.time);
  const priceCoordinate =
    data.price && candlestickSeriesRef.current?.priceToCoordinate(data.price);

  if (timeCoordinate != null && priceCoordinate != null) {
    const arrowMarker = document.createElement('div');
    arrowMarker.id = `marker-${data.time}-${data.price}`.replace(/\./g, '_'); // Unique ID for the marker
    arrowMarker.style.position = 'absolute';
    arrowMarker.style.left = `${timeCoordinate - 2}px`;
    arrowMarker.style.top = `${priceCoordinate}px`;
    arrowMarker.style.transform = 'translate(-50%, -100%)';
    arrowMarker.style.width = '0';
    arrowMarker.style.height = '0';
    arrowMarker.style.borderTop = '3.5px solid transparent';
    arrowMarker.style.borderBottom = '3.5px solid transparent';
    arrowMarker.style.borderLeft = `7px solid ${color}`;
    arrowMarker.style.zIndex = '10';

    return arrowMarker;
  }
};

export const createMarkers = (
  data: any[],
  side: Side | null,
  color: string,
  options?: { idPrefix?: string }
): SeriesMarker<Time>[] => {
  return data.map((item, index) => ({
    time: item.time,
    position: side === 'LONG' ? 'belowBar' : 'aboveBar',
    color,
    shape: side === 'LONG' ? 'arrowUp' : 'arrowDown',
    id: options?.idPrefix ? `${options.idPrefix}-${item.time}-${index}` : undefined
  }));
};

export const createAndAppendMarkers = <
  T extends { time: Time; exitPrice?: number | null }
>(
  dataArray: T[],
  color: string,
  chartRef: React.MutableRefObject<IChartApi | null>,
  candlestickSeriesRef: React.MutableRefObject<ISeriesApi<'Candlestick'> | null>,
  chartContainerRef: React.MutableRefObject<HTMLDivElement | null>
): HTMLDivElement[] => {
  const markers: HTMLDivElement[] = [];

  dataArray.forEach(({ time, exitPrice }) => {
    const marker = createArrowMarker(
      { time, price: exitPrice },
      chartRef,
      candlestickSeriesRef,
      { color }
    );
    if (marker) {
      chartContainerRef.current?.appendChild(marker);
      markers.push(marker);
    }
  });

  return markers;
};

export const updateMarkerPosition = <
  T extends { time: Time; exitPrice?: number | null }
>(
  dataArray: T[],
  chartRef: React.MutableRefObject<IChartApi | null>,
  candlestickSeriesRef: React.MutableRefObject<ISeriesApi<'Candlestick'> | null>
): void => {
  dataArray.forEach(({ time, exitPrice }) => {
    const timeCoordinate = chartRef.current?.timeScale().timeToCoordinate(time);
    const priceCoordinate =
      exitPrice && candlestickSeriesRef.current?.priceToCoordinate(exitPrice);

    const markerId = `marker-${time}-${exitPrice}`.replace(/\./g, '_');
    const marker = document.querySelector(`#${markerId}`) as HTMLElement | null;

    if (marker && timeCoordinate != null && priceCoordinate != null) {
      marker.style.left = `${timeCoordinate - 2}px`;
      marker.style.top = `${priceCoordinate}px`;
    }
  });
};
